import {isHeaderRedesign} from './web-vitals'
import type {MetricOrHPC} from './web-vitals'
import {loaded} from '@github-ui/document-ready'
import {ssrSafeDocument} from '@github-ui/ssr-utils'
import {sendStats, sendCustomMetric} from '@github-ui/stats'
import {sendToHydro} from './hydro-stats'
import {isFeatureEnabled} from '@github-ui/feature-flags'
import {MECHANISM_MAPPING} from '@github-ui/soft-nav/stats'
import {getCPUBucket} from '@github-ui/cpu-bucket'
import type {HPCTimingEvent} from './hpc-events'
import {getDomNodes} from './dom-nodes'
import type {INPAttribution} from './inp/metric'
import type {LCPMetricWithAttribution} from 'web-vitals/attribution'
import {getLCPBreakdown} from './utils/lcp-breakdown'
import {shouldReportWebVital} from './utils/suppression'

type INPBottleneck = 'input_delay' | 'processing' | 'presentation'

/**
 * Determines the primary bottleneck phase for an INP interaction.
 * When phases have equal duration, precedence is: processing > input_delay > presentation
 * @returns The bottleneck phase identifier, or undefined if attribution data is incomplete
 */
function getBottleneck(attribution: INPAttribution | undefined): INPBottleneck | undefined {
  if (!attribution) return undefined
  const {inputDelay, processingDuration, presentationDelay} = attribution
  if (inputDelay === undefined || processingDuration === undefined || presentationDelay === undefined) {
    return undefined
  }

  if (processingDuration >= inputDelay && processingDuration >= presentationDelay) return 'processing'
  if (inputDelay >= presentationDelay) return 'input_delay'
  return 'presentation'
}

interface NetworkInformation extends EventTarget {
  readonly effectiveType: string
}

interface SendVitalsOptions {
  url?: string
}

export function sendVitals(metric: MetricOrHPC, opts: SendVitalsOptions = {}) {
  // Check if this metric should be reported based on execution conditions and thresholds
  // This suppresses metrics collected during non-representative execution (debugger pauses,
  // page backgrounding, extreme outliers) to protect Datadog percentile accuracy.
  // Suppression telemetry is sent via BROWSER_VITALS_SUPPRESSED custom metric.
  if (!shouldReportWebVital(metric)) {
    return
  }

  const {name, value} = metric
  const stat: PlatformBrowserPerformanceWebVitalTiming = {
    name: opts.url || window.location.href,
    cpu: getCPUBucket(),
  }
  stat[name.toLowerCase() as Lowercase<typeof name>] = value

  if (isFeatureEnabled('sample_network_conn_type')) {
    stat.networkConnType = getConnectionType()
  }

  if (name === 'ElementTiming') {
    stat.identifier = metric.identifier
  }

  if (name === 'HPC') {
    addHPCStats(stat, metric)
  } else if (name === 'LCP') {
    addLCPBreakdown(stat, metric)
  }

  // Only set custom fields if they exist (from our custom INPMetric class)
  if (name === 'INP' && 'interactionType' in (metric.attribution || {})) {
    const attribution = metric.attribution as INPAttribution | undefined
    stat.inpInteractionType = attribution?.interactionType
    stat.inpEventType = attribution?.eventType
    stat.inpBottleneck = getBottleneck(attribution)

    // Get DOM node counts once for both stat and phase custom metrics
    const domNodeCounts = getDomNodes()

    if (domNodeCounts.current !== undefined) {
      stat.domNodes = domNodeCounts.current
    }
    if (domNodeCounts.previous !== undefined) {
      stat.previousDomNodes = domNodeCounts.previous
    }

    // Send separate INP phase duration metrics as custom metrics
    const phaseTags: Record<string, string | number> = {
      cpu: getCPUBucket(),
    }
    if (domNodeCounts.current !== undefined) {
      phaseTags.domNodes = domNodeCounts.current
    }
    if (domNodeCounts.previous !== undefined) {
      phaseTags.previousDomNodes = domNodeCounts.previous
    }

    if (attribution?.inputDelay !== undefined) {
      sendCustomMetric({
        name: 'BROWSER_VITALS_DIST_INP_INPUT_DELAY',
        value: attribution.inputDelay,
        tags: phaseTags,
        requestUrl: opts.url,
      })
    }
    if (attribution?.processingDuration !== undefined) {
      sendCustomMetric({
        name: 'BROWSER_VITALS_DIST_INP_PROCESSING',
        value: attribution.processingDuration,
        tags: phaseTags,
        requestUrl: opts.url,
      })
    }
    if (attribution?.presentationDelay !== undefined) {
      sendCustomMetric({
        name: 'BROWSER_VITALS_DIST_INP_PRESENTATION',
        value: attribution.presentationDelay,
        tags: phaseTags,
        requestUrl: opts.url,
      })
    }
  }

  // Only get domNodes for HPC here, INP already handled above
  if (name === 'HPC') {
    const domNodeCounts = getDomNodes()
    stat.domNodes = domNodeCounts.current
    stat.previousDomNodes = domNodeCounts.previous
  }

  const syntheticTest = document.querySelector('meta[name="synthetic-test"]')
  if (syntheticTest) {
    stat.synthetic = true
  }

  // Always emit DOM events, Hydro, and staff bar with the full stat
  emitEvent(name, stat)

  // Datadog: session sampling is handled inside sendStats
  sendStats({webVitalTimings: [stat]})

  sendToHydro({metric, ssr: !!stat.ssr, domNodes: stat.domNodes, previousDomNodes: stat.previousDomNodes})

  updateStaffBar(name, value)
}

function emitEvent(name: string, stat: PlatformBrowserPerformanceWebVitalTiming) {
  const eventName = `web-vitals:${name.toLowerCase()}`
  ssrSafeDocument?.dispatchEvent(new CustomEvent(eventName, {detail: stat}))
}

const addLCPBreakdown = (stat: PlatformBrowserPerformanceWebVitalTiming, metric: LCPMetricWithAttribution) => {
  const lcpBreakdown = getLCPBreakdown(metric)
  stat.lcpBreakdown = lcpBreakdown
}

const addHPCStats = (stat: PlatformBrowserPerformanceWebVitalTiming, metric: HPCTimingEvent) => {
  stat.soft = metric.soft
  stat.ssr = metric.ssr
  stat.mechanism = MECHANISM_MAPPING[metric.mechanism]
  stat.lazy = metric.lazy
  stat.alternate = metric.alternate
  stat.hpcFound = metric.found
  stat.hpcGqlFetched = metric.gqlFetched
  stat.hpcJsFetched = metric.jsFetched
  stat.headerRedesign = isHeaderRedesign()
  stat.app = metric.app
}

function updateStaffBar(name: string, value: number) {
  const staffBarContainer = document.querySelector('#staff-bar-web-vitals')
  const metricContainer = staffBarContainer?.querySelector(`[data-metric=${name.toLowerCase()}]`)

  if (!metricContainer) {
    return
  }

  metricContainer.textContent = value.toPrecision(6)
}

function isTimingSuppported(): boolean {
  return !!(window.performance && window.performance.timing && window.performance.getEntriesByType)
}

function getConnectionType() {
  if (
    'connection' in navigator &&
    navigator.connection &&
    'effectiveType' in (navigator.connection as NetworkInformation)
  ) {
    return (navigator.connection as NetworkInformation).effectiveType
  }

  return 'N/A'
}

export async function sendTimingResults() {
  if (!isTimingSuppported()) return

  await loaded
  await new Promise(resolve => setTimeout(resolve))

  sendResourceTimings()
  sendNavigationTimings()
}

const sendResourceTimings = () => {
  const resourceTimings = window.performance.getEntriesByType('resource').map(
    (timing): PlatformBrowserPerformanceNavigationTiming => ({
      name: timing.name,
      entryType: timing.entryType,
      startTime: timing.startTime,
      duration: timing.duration,
      initiatorType: timing.initiatorType,
      nextHopProtocol: timing.nextHopProtocol,
      workerStart: timing.workerStart,
      redirectStart: timing.redirectStart,
      redirectEnd: timing.redirectEnd,
      fetchStart: timing.fetchStart,
      domainLookupStart: timing.domainLookupStart,
      domainLookupEnd: timing.domainLookupEnd,
      connectStart: timing.connectStart,
      connectEnd: timing.connectEnd,
      secureConnectionStart: timing.secureConnectionStart,
      requestStart: timing.requestStart,
      responseStart: timing.responseStart,
      responseEnd: timing.responseEnd,
      transferSize: timing.transferSize,
      encodedBodySize: timing.encodedBodySize,
      decodedBodySize: timing.decodedBodySize,
    }),
  )

  if (resourceTimings.length) {
    sendStats({resourceTimings}, false, 0.05)
  }
}

const sendNavigationTimings = () => {
  const navigationTimings = window.performance.getEntriesByType('navigation').map(
    (timing): PlatformBrowserPerformanceNavigationTiming => ({
      activationStart: timing.activationStart,
      name: timing.name,
      entryType: timing.entryType,
      startTime: timing.startTime,
      duration: timing.duration,
      initiatorType: timing.initiatorType,
      nextHopProtocol: timing.nextHopProtocol,
      workerStart: timing.workerStart,
      redirectStart: timing.redirectStart,
      redirectEnd: timing.redirectEnd,
      fetchStart: timing.fetchStart,
      domainLookupStart: timing.domainLookupStart,
      domainLookupEnd: timing.domainLookupEnd,
      connectStart: timing.connectStart,
      connectEnd: timing.connectEnd,
      secureConnectionStart: timing.secureConnectionStart,
      requestStart: timing.requestStart,
      responseStart: timing.responseStart,
      responseEnd: timing.responseEnd,
      transferSize: timing.transferSize,
      encodedBodySize: timing.encodedBodySize,
      decodedBodySize: timing.decodedBodySize,
      unloadEventStart: timing.unloadEventStart,
      unloadEventEnd: timing.unloadEventEnd,
      domInteractive: timing.domInteractive,
      domContentLoadedEventStart: timing.domContentLoadedEventStart,
      domContentLoadedEventEnd: timing.domContentLoadedEventEnd,
      domComplete: timing.domComplete,
      loadEventStart: timing.loadEventStart,
      loadEventEnd: timing.loadEventEnd,
      type: timing.type,
      redirectCount: timing.redirectCount,
    }),
  )

  if (navigationTimings.length) {
    sendStats({navigationTimings}, false, isDevelopment() ? 1 : 0.05)
  }
}

function isDevelopment() {
  return process?.env?.NODE_ENV === 'development'
}
